import React, { useState, useEffect, useRef } from 'react';
import { useTx } from '../hooks/useTx';
import { useContracts } from '../hooks/useContract';
import { PACKAGES, ALL_PACKAGES_TOTAL } from '../config/contracts';
import { usd, numUSDT } from '../utils/format';

export default function Packages({ data }) {
  const { run, pending } = useTx();
  const { evittaRead } = useContracts();
  const { userId } = data;
  const active = (data.packages || []).map((b) => !!b);
  const allActive = active.length === 9 && active.every(Boolean);

  // Per-package ceiling details (isActive, totalInvested, totalEarned, ceilingLimit)
  const [details, setDetails] = useState({});
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => {
    setReloadKey((k) => k + 1);
    data.refresh();
  };

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!evittaRead || !userId) return undefined;
    let cancelled = false;
    (async () => {
      const out = {};
      await Promise.all(
        PACKAGES.map(async (p) => {
          const d = await evittaRead.userPackages(userId, p.id).catch(() => null);
          if (d) {
            out[p.id] = {
              earned: numUSDT(d.totalEarned),
              ceiling: numUSDT(d.ceilingLimit),
              invested: numUSDT(d.totalInvested),
            };
          }
        })
      );
      if (!cancelled && mounted.current) setDetails(out);
    })();
    return () => { cancelled = true; };
  }, [evittaRead, userId, reloadKey]);

  const upgrade = (id, price) =>
    run({ method: 'upgradePackage', args: [id], approveAmount: price, successMsg: `Package ${id} activated!`, onDone: reload });
  const retopup = (id, price) =>
    run({ method: 'retopupPackage', args: [id], approveAmount: price, successMsg: `Package ${id} re-topped up!`, onDone: reload });
  const retopupAll = () =>
    run({ method: 'retopupAllPackages', args: [], approveAmount: ALL_PACKAGES_TOTAL, successMsg: 'All packages re-topped up!', onDone: reload });

  return (
    <div className="tab fade-in">
      {/* One-shot re-topup — moved to TOP */}
      {allActive && (
        <div className="card glow" style={{ marginTop: 14 }}>
          <div className="card-row">
            <div>
              <div style={{ fontWeight: 700 }}>One-Shot Re-Topup</div>
              <div className="dim" style={{ fontSize: 12 }}>Refresh all 9 ceilings in one transaction.</div>
            </div>
            <div className="stat-value gold" style={{ fontSize: 18 }}>${ALL_PACKAGES_TOTAL}</div>
          </div>
          <div style={{ marginTop: 14 }}>
            <button className="btn" onClick={retopupAll} disabled={pending}>
              {pending ? <span className="spin-inline" /> : null}
              {pending ? 'Processing…' : 'Re-Topup All Packages'}
            </button>
          </div>
        </div>
      )}

      <div className="section-title"><span className="bar" />Your Packages</div>

      <div style={{ display: 'grid', gap: 12 }}>
        {PACKAGES.map((pkg, i) => {
          const isOn = active[i];
          const prevOn = i === 0 ? true : active[i - 1];
          const canUpgrade = !isOn && prevOn;
          const d = details[pkg.id];
          const earned = d ? d.earned : 0;
          const ceiling = d ? d.ceiling : 0;
          const available = Math.max(0, ceiling - earned);
          const pct = ceiling > 0 ? Math.min(100, (earned / ceiling) * 100) : 0;
          const full = ceiling > 0 && earned >= ceiling;

          return (
            <div key={pkg.id} className={`pkg ${isOn ? 'active' : ''}`}>
              <div className="card-row">
                <div>
                  <span className="pkg-id">PKG {pkg.id}</span>
                  <div className="pkg-name">{pkg.name}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="pkg-price" style={{ margin: 0 }}>${pkg.price}</div>
                  {isOn ? <span className="pill ok">Active</span> : <span className="pill off">Locked</span>}
                </div>
              </div>

              {/* Ceiling details + progress — only meaningful when active */}
              {isOn && (
                <div style={{ marginTop: 12 }}>
                  <div className="grid-3">
                    <Mini label="Earned" value={usd(earned)} />
                    <Mini label="Ceiling" value={usd(ceiling)} />
                    <Mini label="Available" value={usd(available)} />
                  </div>
                  <div className="bar-track">
                    <div className={`bar-fill ${full ? 'full' : ''}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="dim" style={{ fontSize: 11, marginTop: 5, textAlign: 'right' }}>
                    {pct.toFixed(0)}% of ceiling used{full ? ' · limit reached' : ''}
                  </div>
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                {isOn ? (
                  <button className="btn blue sm" style={{ width: '100%' }} onClick={() => retopup(pkg.id, pkg.price)} disabled={pending}>
                    Re-Topup (+${pkg.price})
                  </button>
                ) : canUpgrade ? (
                  <button className="btn sm" style={{ width: '100%' }} onClick={() => upgrade(pkg.id, pkg.price)} disabled={pending}>
                    {pending ? <span className="spin-inline" /> : null} Upgrade · ${pkg.price}
                  </button>
                ) : (
                  <button className="btn ghost sm" style={{ width: '100%' }} disabled>
                    Unlock PKG {pkg.id - 1} first
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div className="dim" style={{ fontSize: 10.5 }}>{label}</div>
      <div style={{ fontWeight: 700, fontSize: 13, marginTop: 2 }}>{value}</div>
    </div>
  );
}
